import { useState } from 'react'
import { snippetUpdateSchema } from '@/lib/validation'

interface SnippetFormProps {
  initialValue?: string
  onSubmit: (content: string) => Promise<void>
  onCancel?: () => void
  submitLabel?: string
  placeholder?: string
}

export const SnippetForm = ({
  initialValue = '',
  onSubmit,
  onCancel,
  submitLabel = 'Add Snippet',
  placeholder = 'What did you do today?',
}: SnippetFormProps) => {
  const [content, setContent] = useState(initialValue)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validation = snippetUpdateSchema.safeParse({ content })
    if (!validation.success) {
      setError(validation.error.errors[0]?.message || 'Invalid content')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await onSubmit(validation.data.content)
      setContent('')
    } catch (err: any) {
      setError(err.message || 'Failed to save snippet')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="input resize-none"
          disabled={isSubmitting}
        />
        <div className="flex justify-between mt-1">
          <p className="text-xs text-gray-500">{content.length}/500</p>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      </div>

      <div className="flex space-x-2">
        <button
          type="submit"
          disabled={isSubmitting || !content.trim()}
          className="btn-primary"
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="btn-secondary"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
